// client/src/components/teams/TeamComparisonTable.tsx
//
// Tabla comparativa del team: una fila por jugador con sus stats clave
// y color-coding por benchmark (verde/amarillo/rojo).
// Feature A2 del coaching dashboard.

import React from 'react';
import { useT } from '../../lib/i18n';
import { useBenchmarks } from '../../contexts/BenchmarkContext';
import { payloadToDerived } from './payloadToStats';

interface PlayerRow {
  userId:       string | null;
  email:        string | null;
  handsCount:   number;
  lastSync:     string | null;
  statsPayload: Record<string, any>;
}

interface Props {
  players:         PlayerRow[];
  onViewPlayer:    (userId: string | null) => void;
}

// ── Colore-coding de celdas ───────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  ok:       '#52c2a0',
  warn:     '#e0a852',
  critical: '#e05252',
};

function StatVal({ statKey, value }: { statKey: string; value: number | null }) {
  const bm = useBenchmarks();
  if (value === null) return <span style={{ color: 'var(--muted)' }}>—</span>;
  if (value === 0) return <span style={{ color: 'var(--muted)', fontWeight: 600 }}>0%</span>;
  const color = STATUS_COLORS[bm.evalStat(statKey, value, 'all')] ?? 'var(--text)';
  return <span style={{ color, fontWeight: 600 }}>{value}%</span>;
}

// ── Componente ─────────────────────────────────────────────────────────────────

export function TeamComparisonTable({ players, onViewPlayer }: Props) {
  const { t } = useT();

  // Calcular derived stats por jugador
  const rows = players.map(p => ({
    ...p,
    derived: payloadToDerived(p.statsPayload),
  }));

  const hasSyncedData = rows.some(r => r.derived !== null);

  if (!hasSyncedData) {
    return (
      <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
        {t('teams.comparisonNoData')}
      </p>
    );
  }

  const thStyle: React.CSSProperties = {
    textAlign: 'right', padding: '0.5rem 0.75rem', color: 'var(--muted)',
    fontSize: '0.65rem', letterSpacing: '1px', fontWeight: 400,
    whiteSpace: 'nowrap',
  };
  const thLeft: React.CSSProperties = { ...thStyle, textAlign: 'left' };

  const tdStyle: React.CSSProperties = {
    padding: '0.65rem 0.75rem', textAlign: 'right',
    fontSize: '0.82rem', borderBottom: '1px solid #0d1117',
  };
  const tdLeft: React.CSSProperties = { ...tdStyle, textAlign: 'left', color: 'var(--text)' };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #1e2636' }}>
            <th style={thLeft}>{t('teams.colPlayer')}</th>
            <th style={thStyle}>VPIP</th>
            <th style={thStyle}>PFR</th>
            <th style={thStyle}>3Bet</th>
            <th style={thStyle}>{t('teams.colCbet')}</th>
            <th style={thStyle}>{t('teams.colFoldTo3bet')}</th>
            <th style={thStyle}>{t('teams.colFoldToPush')}</th>
            <th style={thStyle}>{t('teams.colHands')}</th>
            <th style={thStyle} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const d = row.derived;
            return (
              <tr key={row.userId ?? i} style={{ borderBottom: '1px solid #0d1117' }}>
                <td style={tdLeft}>
                  <button
                    onClick={() => onViewPlayer(row.userId)}
                    style={{
                      background: 'none', border: 'none', color: 'var(--text)',
                      fontFamily: 'var(--mono)', fontSize: '0.8rem', cursor: d ? 'pointer' : 'default',
                      padding: 0, textAlign: 'left',
                      textDecoration: d ? 'underline' : 'none',
                      textDecorationColor: '#4a9ae040',
                    }}
                    disabled={!d}
                    title={d ? t('teams.viewPlayerStats') : undefined}
                  >
                    {row.email ?? '—'}
                  </button>
                </td>
                <td style={tdStyle}>{d ? <StatVal statKey="vpip"       value={d.vpip}          /> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td style={tdStyle}>{d ? <StatVal statKey="pfr"        value={d.pfr}           /> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td style={tdStyle}>{d ? <StatVal statKey="threebet"   value={d.threebet}      /> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td style={tdStyle}>{d ? <StatVal statKey="cbetFlop"   value={d.cbetFlop}      /> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td style={tdStyle}>{d ? <StatVal statKey="foldTo3bet" value={d.foldTo3bet}    /> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td style={tdStyle}>{d ? <StatVal statKey="foldToPush" value={d.foldToPushOpp > 20 ? d.foldToPush : null} /> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                <td style={{ ...tdStyle, color: 'var(--muted)', fontSize: '0.75rem' }}>
                  {row.handsCount > 0 ? row.handsCount.toLocaleString() : '—'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right' }}>
                  {d && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: '0.68rem', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap' }}
                      onClick={() => onViewPlayer(row.userId)}
                    >
                      {t('teams.viewPlayerStats')}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Leyenda de colores */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.65rem', color: 'var(--muted)' }}>
        <span><span style={{ color: '#52c2a0' }}>■</span> {t('teams.legendOk')}</span>
        <span><span style={{ color: '#e0a852' }}>■</span> {t('teams.legendWarn')}</span>
        <span><span style={{ color: '#e05252' }}>■</span> {t('teams.legendCritical')}</span>
      </div>
    </div>
  );
}
